import { ROUTE_DEFINITIONS, RouteDefinition } from '@/constants/routes';

/**
 * Derive ROUTE_METADATA for AI tools
 * AUTO-GENERATED from ROUTE_DEFINITIONS
 * Maps path → metadata: { '/dashboard': { aiNavigable: true, ... }, ... }
 */
export const ROUTE_METADATA = Object.entries(ROUTE_DEFINITIONS).reduce(
  (acc, [_, def]) => {
    const { path, ...metadata } = def;
    acc[path] = metadata;
    return acc;
  },
  {} as Record<string, Omit<RouteDefinition, 'path'>>
);

/**
 * Auto-generate AI-navigable destinations from metadata
 * This is used by the AI navigation tool
 */
export const generateAiDestinations = () => {
  return Object.entries(ROUTE_METADATA)
    .filter(([_, meta]) => meta.aiNavigable)
    .reduce(
      (acc, [route, meta]) => {
        if (!meta.displayName) {
          throw new Error(
            `Route ${route} is marked aiNavigable but missing displayName`
          );
        }
        const key = meta.displayName.toUpperCase();
        acc[key] = route;
        return acc;
      },
      {} as Record<string, string>
    );
};

/**
 * Generate the enum list for the navigation tool
 * Returns lowercase display names (e.g., ['dashboard', 'tasks', ...])
 */
export const generateAiNavigationEnum = (): string[] => {
  return Object.entries(ROUTE_METADATA)
    .filter(([_, meta]) => meta.aiNavigable)
    .map(([_, meta]) => meta.displayName!)
    .sort();
};

/**
 * Generate the description string for the navigation tool
 * Returns formatted string: "dashboard (home/overview), tasks (to-do items), ..."
 */
export const generateAiNavigationDescription = (): string => {
  const descriptions = Object.entries(ROUTE_METADATA)
    .filter(([_, meta]) => meta.aiNavigable)
    .map(([_, meta]) => `${meta.displayName} (${meta.description})`)
    .sort();

  return `The page to navigate to. Options: ${descriptions.join(', ')}`;
};

/**
 * Generate the pageMap for the navigation tool handler
 * Maps display names to route paths
 */
export const generateAiPageMap = (): Record<string, string> => {
  return Object.entries(ROUTE_METADATA)
    .filter(([_, meta]) => meta.aiNavigable)
    .reduce(
      (acc, [route, meta]) => {
        acc[meta.displayName!] = route;
        return acc;
      },
      {} as Record<string, string>
    );
};
